/**
 * extGState — register PDF Extended-Graphics-State dictionaries for stroke +
 * fill alpha (PDF-6.3b) and blend mode (PDF-6.4 "Lines merge" / merge control).
 *
 * AutoCAD's CTB/STB screening percentage ∈ (0, 100) becomes a translucent paint
 * in the PDF: `/CA` (stroke alpha) + `/ca` (fill alpha) on an ExtGState dict
 * registered in the page's Resources `/ExtGState` subdictionary. The renderer
 * wraps the affected primitive in `q /GSaN gs … Q` so the alpha applies only
 * to that paint op without leaking into subsequent batched groups.
 *
 * PDF-6.4 adds "Lines merge" parity for AutoCAD's PC3 plot setting: when the
 * user picks "Merge" (vs "Overwrite"), every painted primitive is wrapped in
 * an ExtGState carrying `/BM /Multiply` so overlapping geometry darkens like
 * AutoCAD's Lines-merge behaviour. The same `ExtGState` mechanism (PDF 32000-
 * 1:2008 §8.4.5) accommodates both `/CA` `/ca` (alpha) and `/BM` (blend mode)
 * in one dict; we therefore extend the helper to register a dict carrying
 * EITHER or BOTH and dedup by `(alpha, blendMode)`.
 *
 * # API
 *
 *   const gsName = ensureExtGState(page, { alpha: 0.5, blendMode: "Multiply" });
 *   // → "GSa500m" (stable, dedup'd name for alpha=0.5 + Multiply)
 *   page.pushOperators(
 *     pushGraphicsState(),
 *     PDFOperator.of(PDFOperatorNames.SetGraphicsStateParameters, [PDFName.of(gsName)]),
 *     // … the paint operators that should be translucent + multiplied …
 *     popGraphicsState(),
 *   );
 *
 * The legacy {@link ensureAlphaExtGState} alpha-only wrapper is preserved as a
 * thin shim so existing call sites stay byte-identical.
 *
 * # Dedup contract
 *
 * Each `(page, alpha, blendMode)` triple maps to ONE ExtGState dict. Alpha is
 * rounded to 3 decimal places before keying (FP noise collapses); blend mode is
 * a literal string. The resource NAME is derived from the SAME 3-dp value as a
 * per-mille integer so the name is 1:1 with the cache key — two alphas that
 * differ at the 3rd decimal (e.g. 0.500 vs 0.502) get DISTINCT names
 * (`GSa500` vs `GSa502`) and therefore never collide into one dict. Resource
 * names:
 *   - alpha only (Normal blend mode):           `GSa<permille>`   (e.g. GSa500)
 *   - blend mode only (alpha === 1):            `GSm`             (new)
 *   - alpha + Multiply:                         `GSa<permille>m`  (e.g. GSa500m)
 *
 * Per-mille range: 0.0 → `GSa0`, 0.5 → `GSa500`, 1.0 → `GSa1000`. The "m"
 * suffix is stable for Multiply; future blend modes (Darken, Screen, …) would
 * extend with their own single-letter suffix and a new cache key.
 *
 * # PDF spec reference
 *
 *   - PDF 32000-1:2008 §8.4.5 "Graphics state parameter dictionaries"
 *   - `/CA` — stroking alpha (number ∈ [0, 1])
 *   - `/ca` — non-stroking (fill) alpha (number ∈ [0, 1])
 *   - `/BM` — current blend mode (name or array of names); `/Multiply` is the
 *     standard AutoCAD-parity merge-control compositing op.
 *   - `gs` operator — load the dict by its resource name.
 */

import {
  PDFDict,
  PDFName,
  PDFNumber,
  type PDFPage,
} from "pdf-lib";

/** Blend mode values supported by this helper. Today only `"Multiply"`. */
export type ExtGStateBlendMode = "Multiply";

/** Options accepted by {@link ensureExtGState}. */
export interface EnsureExtGStateOptions {
  /**
   * Stroke + fill alpha (`/CA` + `/ca`) in `[0, 1]`. When omitted OR equal to 1
   * the dict carries NO alpha keys (so a Multiply-only dict is `<< /BM /Multiply >>`).
   * Rounded to 3 decimals for cache keying.
   */
  alpha?: number;
  /**
   * Blend mode (`/BM`). When omitted the dict carries no `/BM` key (Normal is
   * the PDF default, no-op). When `"Multiply"`, the dict carries `/BM /Multiply`.
   */
  blendMode?: ExtGStateBlendMode;
}

/**
 * Per-page cache of `cacheKey → resourceName` so repeat calls with the same
 * `(alpha, blendMode)` triple return the same name without re-walking the
 * Resources dict. Keyed by the page object (WeakMap) so a multi-page PUBLISH
 * carries one cache per page — each page's ExtGState resource map is independent.
 */
const PAGE_GS_CACHE = new WeakMap<PDFPage, Map<string, string>>();

/**
 * Round an alpha to 3 decimals (FP-noise stable) and stringify with a fixed 3-dp
 * form so the cache key is canonical (`"0.500"` not `"0.5"`).
 */
function alphaCacheToken(alpha: number | undefined): string {
  if (alpha === undefined) return "none";
  const rounded = Math.max(0, Math.min(1, Math.round(alpha * 1000) / 1000));
  return rounded.toFixed(3);
}

/** Build the canonical cache key for `(alpha, blendMode)`. */
function cacheKey(alpha: number | undefined, blendMode: ExtGStateBlendMode | undefined): string {
  return `${alphaCacheToken(alpha)}|${blendMode ?? "Normal"}`;
}

/**
 * Build the stable resource name for `(alpha, blendMode)`. The alpha component
 * of the name is the SAME 3-dp value the cache key uses, expressed as a
 * per-mille INTEGER (`Math.round(rounded × 1000)`), so the name is 1:1 with the
 * cache key: two alphas that round to distinct 3-dp values (e.g. 0.500 vs
 * 0.502) get DISTINCT names (`GSa500` vs `GSa502`) and therefore distinct page
 * `/ExtGState` dicts — they never collide. (A prior 2-sig-fig `GSa<percent>`
 * form mapped 0.500 and 0.502 BOTH to `GSa50`, so the second registration
 * overwrote the first dict under the shared name while the cache still handed
 * out two names — the first run's `gs` op then pointed at the WRONG dict. With
 * entity DXF-440 alpha (256 levels) that collision is reachable.) A Multiply
 * blend mode with a translucent alpha appends `"m"`; a Multiply-only call (no
 * alpha) yields `"GSm"`.
 */
function resourceName(alpha: number | undefined, blendMode: ExtGStateBlendMode | undefined): string {
  const hasAlpha = alpha !== undefined;
  const hasBM = blendMode !== undefined;
  if (hasAlpha) {
    const rounded = Math.max(0, Math.min(1, Math.round(alpha! * 1000) / 1000));
    const perMille = Math.round(rounded * 1000);
    return hasBM ? `GSa${perMille}m` : `GSa${perMille}`;
  }
  if (hasBM) return "GSm";
  // Defensive default — a no-op dict. Callers normally avoid this (it produces
  // an `<< /Type /ExtGState >>` shell with no keys), but we still register so
  // the contract "returns a resource name" never has to throw.
  return "GSnoop";
}

/**
 * Resolve (or create) the ExtGState resource for `opts` on `page`, returning
 * the resource name to feed into a `gs` operator. The dict carries the requested
 * combination of `/CA` `/ca` (alpha) and `/BM` (blend mode); BOTH fields are
 * independently optional.
 *
 * Dedup by `(alpha-rounded-to-3dp, blendMode)` per page — a repeated call with
 * the same triple returns the SAME resource name and does NOT register a new
 * dict (matches the historic alpha-only dedup contract).
 */
export function ensureExtGState(
  page: PDFPage,
  opts: EnsureExtGStateOptions,
): string {
  const { alpha, blendMode } = opts;
  const key = cacheKey(alpha, blendMode);
  const name = resourceName(alpha, blendMode);

  let cache = PAGE_GS_CACHE.get(page);
  if (!cache) {
    cache = new Map<string, string>();
    PAGE_GS_CACHE.set(page, cache);
  }
  const existing = cache.get(key);
  if (existing) return existing;

  const context = page.doc.context;

  // Build the ExtGState dict. We emit `/CA` `/ca` ONLY when the alpha is in
  // [0, 1) (a `/CA 1 /ca 1` dict is functionally identical to omitting them
  // and bloats the output). `/BM /Multiply` is emitted only when explicitly
  // requested — Normal is the PDF default and needs no key. The dict is built
  // empty + `set()` per field so the literal-array typing of `context.obj`
  // doesn't have to widen across the conditional branches.
  const dict = PDFDict.withContext(context);
  dict.set(PDFName.of("Type"), PDFName.of("ExtGState"));
  if (alpha !== undefined && alpha < 1) {
    const rounded = Math.max(0, Math.min(1, Math.round(alpha * 1000) / 1000));
    dict.set(PDFName.of("CA"), PDFNumber.of(rounded));
    dict.set(PDFName.of("ca"), PDFNumber.of(rounded));
  }
  if (blendMode === "Multiply") {
    dict.set(PDFName.of("BM"), PDFName.of("Multiply"));
  }

  const ref = context.register(dict);

  // Attach to the page's Resources `/ExtGState` subdictionary. The renderer
  // always works with a freshly added page (Resources exists), but we defend
  // against an absent Resources dict for structural robustness.
  let resources = page.node.Resources();
  if (!resources) {
    resources = context.obj({});
    page.node.set(PDFName.of("Resources"), resources);
  }
  const found = resources.lookupMaybe(PDFName.of("ExtGState"), PDFDict);
  let extGStateDict: PDFDict;
  if (found instanceof PDFDict) {
    extGStateDict = found;
  } else {
    extGStateDict = context.obj({});
    resources.set(PDFName.of("ExtGState"), extGStateDict);
  }
  extGStateDict.set(PDFName.of(name), ref);

  cache.set(key, name);
  return name;
}

/**
 * Alpha-only legacy wrapper. Thin shim around {@link ensureExtGState} so
 * existing call sites stay unchanged and the alpha-only resource name format
 * (`GSa<permille>`) + dedup contract are preserved.
 *
 * Calling with `alpha === 1` still registers a (degenerate) dict — defensive,
 * matches the historic pre-PDF-6.4 behaviour.
 *
 * @deprecated Use {@link ensureExtGState} for new call sites needing blend mode.
 *             The wrapper is retained for source compatibility.
 */
export function ensureAlphaExtGState(page: PDFPage, alpha: number): string {
  // Historic behaviour for `alpha === 1`: register a `/CA 1 /ca 1` dict named
  // `GSa1000` (per-mille). {@link ensureExtGState} omits the `/CA` `/ca` keys
  // when alpha ≥ 1 (no-op dict), but the resource name stays stable so the
  // cache + the call-site `gs` operator behave identically. The dict-CONTENT
  // change (an empty dict vs `/CA 1 /ca 1`) is a no-op at PDF render-time (both
  // are opaque), so this is safe.
  return ensureExtGState(page, { alpha });
}
